/* =====================================================================
 * WorkspaceModeLayout — 페이지 내부 목록 + 콘텐츠 레이아웃
 * ===================================================================== */

import React, { Suspense, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Menu } from 'lucide-react';
import type { WorkspaceMode } from '@auw/workspace-utils';
import { WorkspaceSidebar } from '../navigation/WorkspaceSidebar';
import { WorkspaceHeader } from '../header/WorkspaceHeader';
import { onAppEvent } from '@auw/stores';
import { useWorkspaceNavigationStore } from '@auw/stores';

interface Props {
  mode: WorkspaceMode;
  children: React.ReactNode;
  inputSlot?: React.ReactNode;
  aboveInputSlot?: React.ReactNode;
}

export const WorkspaceModeLayout: React.FC<Props> = ({ mode, children, inputSlot, aboveInputSlot }) => {
  const { t } = useTranslation('common');
  const pageSidebarOpen = useWorkspaceNavigationStore((s) => s.isPageSidebarOpen(mode));
  const togglePageSidebar = useWorkspaceNavigationStore((s) => () => s.togglePageSidebar(mode));

  useEffect(() => {
    return onAppEvent('workspace:togglePageSidebar', () => togglePageSidebar());
  }, [togglePageSidebar]);

  return (
    <Container>
      <MobileOverlay $open={pageSidebarOpen} onClick={togglePageSidebar} />
      <ListPane $open={pageSidebarOpen}>
        <ListPaneInner $open={pageSidebarOpen}>
          <WorkspaceSidebar
            mode={mode}
            pageSidebarOpen={pageSidebarOpen}
            onTogglePageSidebar={togglePageSidebar}
          />
        </ListPaneInner>
      </ListPane>
      <ContentPane>
        {!pageSidebarOpen && (
          <CollapsedSidebarToggleSlot>
            <CollapsedSidebarToggleBtn
              type="button"
              onClick={togglePageSidebar}
              title={t('workspace.expandPageSidebar')}
            >
              <Menu size={16} />
            </CollapsedSidebarToggleBtn>
          </CollapsedSidebarToggleSlot>
        )}
        <WorkspaceHeader mode={mode} pageSidebarOpen={pageSidebarOpen} />
        <ContentBody>
          <Suspense fallback={<ContentFallback />}>
            {children}
          </Suspense>
        </ContentBody>
        {aboveInputSlot}
        {inputSlot}
      </ContentPane>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;
  min-height: 0;
`;

const MobileOverlay = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: ${({ $open }) => ($open ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: ${({ theme }) => theme.colors.bgOverlay};
    z-index: 30;
  }
`;

const ListPane = styled.aside<{ $open: boolean }>`
  width: ${({ $open, theme }) => ($open ? theme.dm.listWidth : '0')};
  height: 100%;
  min-height: 0;
  overflow: hidden;
  flex-shrink: 0;
  transition: width ${({ theme }) => theme.transitions.normal};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    width: 280px;
    z-index: 31;
    transition: transform ${({ theme }) => theme.transitions.normal};
    transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
  }
`;

const ListPaneInner = styled.div<{ $open: boolean }>`
  min-width: ${({ theme }) => theme.dm.listWidth};
  width: ${({ theme }) => theme.dm.listWidth};
  height: 100%;
  display: flex;
  flex-direction: column;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity ${({ theme }) => theme.transitions.fast};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 280px;
    width: 280px;
    opacity: 1;
    pointer-events: auto;
  }
`;

const CollapsedSidebarToggleBtn = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(33,33,33,0.72)' : 'rgba(255,255,255,0.76)'};
  color: ${({ theme }) => theme.mode === 'dark' ? '#fff' : '#000'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 18px rgba(0,0,0,0.24)'
      : '0 8px 18px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(58,58,58,0.86)' : 'rgba(255,255,255,0.92)'};
    color: ${({ theme }) => theme.mode === 'dark' ? '#fff' : '#000'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const ContentPane = styled.main`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

const ContentBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ContentFallback = styled.div`
  flex: 1;
  min-height: 0;
`;

const CollapsedSidebarToggleSlot = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;
